/* ============================================================
   ZNAKI · audio/index.js
   Warstwa dźwiękowa instalacji — jedno wejście dla pętli sceny.

   Publiczne API (używa go playground/index.js):
     initAudio(root)             — zbuduj silnik, tło i ziarna
     updateAudio(st, dt, specs)  — klatka: poziomy i zdarzenia figur
     pauseAudio()                — wycisz i uśpij (wyjście z widoku, karta w tle)
     resumeAudio()               — powrót
     destroyAudio()              — zwiń warstwy (kontekst i cache zostają)
     getAudioState()             — podgląd dla nakładki ?debug

   Dwie warstwy, dwie magistrale:
     "bg"     — sekwencja akordów tła (background.js)
     "accent" — ziarna przypisane do figur (accents.js), a pod nią
                po jednym łańcuchu na każdą brzmiącą figurę

   Przytrzymanie (ściśnięcie figur do osi) zbiera się na masterze:
   całość wchodzi pod filtr dolnoprzepustowy, a tło pod nim puchnie.

   Ścieżki do plików przychodzą z widoku
   (views/__2026/editionhome/editionhome_playground.php) w atrybucie
   data-playground-params, tak jak w edycjach 2023 i 2024.
   ============================================================ */

import { PARAMS } from '../config';
import { clamp01, lerp } from '../utils';
import AudioEngine from './engine';
import { FILTER_OPEN } from './chain';
import BackgroundLayer from './background';
import AccentLayer from './accents';
import Banks from './banks';

/* Silnik (a z nim kontekst audio i cache próbek) żyje tak długo jak strona.
   Dzięki temu powrót na widok instalacji nie wymaga ponownego odblokowania
   dźwięku ani ponownego pobrania plików — wymieniamy tylko warstwy. */
/* rezonans filtra przy otwartym torze — bez podbicia wokół progu */
const NEUTRAL_Q = 0.7;

let engine = null;
let background = null;
let accents = null;
let onVisibility = null;

/** @return {Boolean} czy warstwa dźwiękowa wystartowała */
export const initAudio = (root) => {
  const params = readAudioParams(root);
  if (!params) return false;

  if (!engine) {
    engine = new AudioEngine({ volume: PARAMS.audio.master });
    if (!engine.supported) { engine = null; return false; }
  }

  if (background) background.destroy();
  if (accents) accents.destroy();

  /* wejście na widok zawsze zaczyna z otwartym filtrem — inaczej
     wyjście w trakcie przytrzymania zostawiłoby całość przymkniętą */
  engine.master.setCutoff(0, 0).setQ(NEUTRAL_Q, 0);

  background = new BackgroundLayer(engine, params.bg || []);
  accents = new AccentLayer(engine, new Banks(params));

  /* tło ma pierwszeństwo w łączu — ziarna dociągają się zaraz po nim */
  background.start().then(() => accents && accents.preload());

  if (!onVisibility) {
    onVisibility = () => (document.hidden ? pauseAudio() : resumeAudio());
    document.addEventListener('visibilitychange', onVisibility);
  }
  return true;
};

export const updateAudio = (st, dt, specs) => {
  if (!engine) return;
  engine.master.setVolume(PARAMS.audio.master, 0.05);
  applyHold(st);
  if (background) background.update(st, dt);
  if (accents) accents.update(specs, st);
};

/* Przytrzymanie — cała instalacja pod filtr.
   st.hold to dokładnie ta sama wartość, która ściąga figury do osi
   (figures.js) i ściska planszę (renderer.js), więc sweep jedzie
   w tempie ich ruchu — tak samo w jedną i w drugą stronę.

   Interpolacja wykładnicza (równe oktawy): liniowa w hercach przez
   większość drogi nie robiłaby nic, a na końcu zapadała się gwałtownie,
   czyli rozjechałaby się z równym ruchem figur. */
const applyHold = (st) => {
  const A = PARAMS.audio;
  const h = clamp01(st.hold);
  const hz = h < 0.002
    ? 0                                     /* 0 = filtr otwarty */
    : FILTER_OPEN * Math.pow(Math.max(40, A.holdCutoff) / FILTER_OPEN, h);
  /* stała czasowa krótsza niż wygładzanie samego st.hold (~0.2 s),
     żeby dźwięk nie wlókł się za obrazem */
  engine.master.setCutoff(hz, 0.02);
  engine.master.setQ(lerp(NEUTRAL_Q, A.holdQ, h), 0.02);
};

export const pauseAudio = () => {
  if (accents) accents.pause();
  if (background) background.pause();
};

export const resumeAudio = () => {
  if (background) background.resume();
  if (accents) accents.resume();
};

/* wyjście z widoku instalacji: milkniemy i usypiamy kontekst,
   ale nie zamykamy go — patrz komentarz przy `engine` */
export const destroyAudio = () => {
  if (onVisibility) document.removeEventListener('visibilitychange', onVisibility);
  onVisibility = null;
  if (accents) accents.destroy();
  if (background) background.destroy();
  accents = null;
  background = null;
  if (engine) engine.suspend();
};

export const getAudioState = () => {
  if (!engine || !background) return null;
  return {
    ...background.state,
    ...(accents ? accents.state : null),
    running: engine.isRunning,
  };
};

/* --- parametry z widoku -------------------------------------- */

const readAudioParams = (root) => {
  if (!root || !root.dataset || !root.dataset.playgroundParams) return null;
  try {
    const params = JSON.parse(root.dataset.playgroundParams);
    return (params && params.audio) || null;
  } catch (e) {
    return null;
  }
};
